import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { isApiError, plainValidationMessage, serverErrorText, type ValidationError } from './errors';
import i18n from '../i18n';
import type { WeightTrendResult } from '../components/weight/weightTrendTypes';
import type { DailyLogEntry } from '../components/daily-log/types';
import type { EngineMessage } from './engineMessage';
import type { Language } from './language';

// ============================================================================
// Type Definitions — aligned with backend (source of truth) and API spec
// ============================================================================

export interface User {
  id: string;
  email: string;
  full_name: string | null;
  subscription_tier: string;
  created_at: string;
  /** Phase 2: shown by the unverified-email banner on the dashboard. */
  email_verified?: boolean;
  /** Phase 4: gates direct navigation to /dashboard etc. — if false, the
   *  app redirects to /onboarding regardless of the requested URL. */
  onboarding_completed?: boolean;
  /** Date-display preference. Governs how dates are rendered app-wide (see
   *  lib/date.ts). Optional so older cached user objects still parse; the
   *  formatter falls back to the ambiguity-proof 'dd-mon-yyyy' when absent. */
  date_format?: 'mdy' | 'dmy' | 'ymd' | 'dd-mon-yyyy';
  /** Weight-display preference. Storage stays kg; this governs presentation only. */
  weight_unit?: 'kg' | 'lb';
  /** Lab-unit preference: the unit lab entry opens in and lab values show in on
   *  screen. Vet documents keep the most-recent-panel rule (labUnitPref.ts). */
  lab_unit?: 'si' | 'conventional';
  /** Language preference: what the app is shown in and the backend writes in. The
   *  server's copy wins after login (lib/language.ts). Optional for older caches. */
  language?: Language;
}

export interface AuthResponse {
  access_token: string;
  refresh_token?: string | null;
  user: User;
}

export interface Cat {
  id: string;
  user_id: string;
  name: string;
  sex: 'male' | 'female' | null;
  breed: string | null;
  date_of_birth: string | null;
  /** From the diagnosis weight READING (findings 16–18). */
  weight_at_diagnosis: number | null;
  /** Scale of the diagnosis reading; null when there is none. */
  diagnosis_weight_method: 'home_scale' | 'vet_scale' | null;
  diagnosis_date: string;
  diagnosis_date_precision: string;
  initial_iris_stage: number | null;
  current_iris_stage: number | null;
  comorbidities: unknown[];
  no_medications_confirmed: boolean;
  photo_filename: string | null;
  created_at: string;
  /** When the owner archived this record (F-08). null for an active cat. */
  archived_at: string | null;
}

export interface LabResult {
  id: string;
  cat_id: string;
  date: string;
  source: string | null;
  creatinine: number;
  creatinine_unit: string;
  sdma: number | null;
  bun: number | null;
  phosphorus: number | null;
  potassium: number | null;
  calcium_total: number | null;
  calcium_ionized: number | null;
  pcv_haematocrit: number | null;
  upc_ratio: number | null;
  urine_specific_gravity: number | null;
  systolic_bp: number | null;
  fgf23: number | null;
  iris_stage_at_time: number | null;
  upc_substage: string | null;
  bp_substage: string | null;
  notes: string | null;
  created_at: string;
}

/**
 * A display-ready, out-of-range lab finding derived from the current lab data.
 * Render `text` verbatim — it already contains the value and the relevant target.
 */
export interface LabFinding {
  parameter: string;
  label: string;
  value: number;
  unit: string;
  severity: 'info' | 'attention' | 'urgent';
  direction: 'above' | 'below';
  /** The English sentence; the fallback for `text_message`. */
  text: string;
  /** The sentence as the engine's code + params — render this, via engineText. */
  text_message?: EngineMessage;
  learn_more_topic?: string;
  /**
   * ISO date of the panel this analyte's value was measured on — not necessarily
   * the most recent panel, since a later one need not have measured this analyte.
   * Also embedded in `text`, where the rendering surface rewrites it into its own
   * date format.
   */
  as_of?: string;
}

export interface ExtractedLabValue {
  field: string;
  value: number;
  unit: string;
  confidence: 'high' | 'medium' | 'low';
  raw_text: string | null;
}

export interface LabOCRResponse {
  extracted_values: ExtractedLabValue[];
  date: string | null;
  source: string | null;
  notes: string | null;
  raw_interpretation: string;
}

export interface DailyLog {
  id: string;
  cat_id: string;
  date: string;
  appetite_score: number | null;
  food_type: string | null;
  water_intake_observation: 'normal' | 'increased' | 'decreased' | null;
  vomiting: boolean;
  vomiting_count: number | null;
  energy_level: number | null;
  medications_given: Record<string, boolean> | null;
  subq_fluids_administered: boolean;
  subq_fluids_volume_ml: number | null;
  notes: string | null;
  created_at: string;
}

/** The per-event daily-care stream (ADR C9): multiple per day, individually fixable. */
export type CareEventType =
  | 'subq_fluids'
  | 'vomiting'
  | 'defecation'
  | 'medication_given'
  | 'medication_skipped'
  | 'glucose'
  | 'resting_respiratory_rate'
  | 'urination';

export interface CareEvent {
  id: string;
  cat_id: string;
  date: string;
  occurred_at: string | null;
  event_type: CareEventType;
  detail: Record<string, unknown>;
  medication_id: string | null;
  care_pass_id: string | null;
  delegate_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateCareEventInput {
  date: string;
  event_type: CareEventType;
  occurred_at?: string;
  detail?: Record<string, unknown>;
  medication_id?: string;
  client_event_id?: string;
}

/** One rotation site's last-used facts (GET /cats/:id/subq-fluids/site-history).
 *  The four rotation sites are always present; never-used ones carry nulls. */
export interface SubqSiteHistoryEntry {
  site: string;
  last_used_date: string | null;
  last_used_at: string | null;
  session_count: number;
}

export interface WeightLog {
  id: string;
  cat_id: string;
  date: string;
  weight_kg: number;
  measurement_method: string | null;
  /** Actor attribution: the pass a caregiver entered this through, and their name
   *  snapshotted at write time. Both null when the owner entered it. */
  care_pass_id?: string | null;
  delegate_name?: string | null;
  created_at: string;
}

export interface QoLAssessment {
  id: string;
  cat_id: string;
  date: string;
  // Optional: the weekly VAS check-in records only a global composite_score.
  appetite_score?: number;
  energy_score?: number;
  comfort_score?: number;
  social_engagement_score?: number;
  overall_score?: number;
  is_monthly_deep: boolean;
  grooming_score: number | null;
  vomiting_frequency: string | null;
  litter_box_notes: string | null;
  medication_tolerance_notes: string | null;
  composite_score: number | null;
  created_at: string;
}

export interface Alert {
  id: string;
  cat_id: string;
  date_generated: string;
  date_acknowledged: string | null;
  date_dismissed: string | null;
  type: string;
  severity: 'info' | 'attention' | 'urgent';
  title: string;
  message_structured: Record<string, unknown>;
  message_rendered: string | null;
  evidence_references: unknown[];
  related_lab_result_id: string | null;
  related_parameter: string | null;
  created_at: string;
}

export interface GettingStartedChecklist {
  has_lab_results: boolean;
  has_daily_logs: boolean;
  has_medications: boolean;
}

export interface SparklinePointDTO {
  date: string;
  value: number;
}

export interface DashboardEngagement {
  today_logged: boolean;
  streak_days: number;
  last_log_date: string | null;
  recent_appetite: SparklinePointDTO[];
  recent_energy: SparklinePointDTO[];
  recent_weights: SparklinePointDTO[];
}

/** A record-keeping reminder (missing / overdue monitoring), derived on the fly. */
export interface Reminder {
  test_name: string | null;
  title: string | null;
  title_message?: EngineMessage | null;
  detail: string | null;
  detail_message?: EngineMessage | null;
  is_overdue: boolean;
  log_action?: { label: string; label_message?: EngineMessage; screen: 'weight' | 'labs' } | null;
}

/** A gentle, review-pending safety-net nudge derived on the fly from a clearly
 *  concerning recent daily log (beta feedback #2). Empty unless the conservative
 *  rule fires. Never a diagnosis — an invitation to consider contacting the vet. */
export interface DailyLogConcern {
  title: string;
  detail: string;
  date: string;
  severity: string;
}

export interface DashboardData {
  cat: Cat;
  latest_lab_result: LabResult | null;
  reminders: Reminder[];
  daily_log_concerns?: DailyLogConcern[];
  latest_qol_score: number | null;
  days_since_last_qol: number | null;
  weight_trend: {
    latest_weight_kg: number;
    latest_date: string;
    entries: Array<{ date: string; weight_kg: number }>;
    /** The engine's derivation — the same object the Weight page renders. */
    derived: WeightTrendResult | null;
  } | null;
  getting_started: GettingStartedChecklist | null;
  engagement: DashboardEngagement;
  /** One line from the signed-off caregiver corpus, rotated daily for the owner.
   *  Rendered verbatim; absent when the server has nothing to offer. */
  caregiver_snippet?: string | null;
  /** One line from the signed-off clinical empowerment-reel corpus, filtered to the
   *  cat's IRIS stage and rotated daily for that cat. Rendered verbatim; absent when
   *  the corpus has nothing for the stage. */
  reel_snippet?: string | null;
  /** Today's log (the cat's local day) with the day's care events rolled in, the
   *  same shape the daily-log endpoints return. Null until today has an entry, and
   *  for an archived record. */
  today_log?: DailyLogEntry | null;
}

/**
 * Guidance comes from the engine, each owner-facing field beside its message. The
 * messages are absent when the backend serves its built-in fallback (engine down).
 */
export interface GuidanceManagementFocus {
  domain: string;
  domain_message?: EngineMessage;
  current_status: string;
  current_status_message?: EngineMessage;
  target: string;
  target_message?: EngineMessage;
  guidance: string;
  guidance_message?: EngineMessage;
  priority: 'critical' | 'high' | 'standard' | 'informational';
  learn_more_topic?: string;
}

export interface GuidanceMonitoringItem {
  test: string;
  test_message?: EngineMessage;
  frequency: string;
  frequency_message?: EngineMessage;
  rationale: string;
  rationale_message?: EngineMessage;
}

export interface GuidanceData {
  stage: number;
  management_focuses: GuidanceManagementFocus[];
  monitoring_schedule: GuidanceMonitoringItem[];
  medication_considerations?: string[];
  medication_considerations_messages?: EngineMessage[];
  nutrition_guidance?: string;
  nutrition_guidance_message?: EngineMessage;
  signs_to_monitor: string[];
  signs_to_monitor_messages?: EngineMessage[];
  qol_considerations: string;
  qol_considerations_message?: EngineMessage;
  evidence_base: string[];
}

export interface VetPrepData {
  cat_name: string;
  iris_stage: number | null;
  summary: string;
  lab_history: unknown[];
  alerts: unknown[];
  medications: unknown[];
  qol_trend: unknown;
  questions_for_vet: unknown[];
  suggested_tests: unknown[];
}

export interface VetVisit {
  id: string;
  cat_id: string;
  visit_date: string;
  next_appointment_date: string | null;
  notes: string | null;
  medication_changes: string | null;
  created_at: string;
  updated_at: string;
}

export interface VetQuestion {
  id: string;
  cat_id: string;
  question_text: string;
  is_answered: boolean;
  created_at: string;
}

export interface CarePass {
  id: string;
  cat_id: string;
  owner_user_id: string;
  delegate_name: string;
  delegate_email: string | null;
  token: string;
  starts_at: string;
  ends_at: string;
  scope_daily_log: boolean;
  scope_weight: boolean;
  scope_view_meds: boolean;
  scope_log_fluids: boolean;
  status: string;
  revoked_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface CarePassDetail extends CarePass {
  activity_summary: {
    daily_log_count: number;
    weight_log_count: number;
    total_days: number;
    missed_days: number;
    last_activity_at: string | null;
    flagged_concerns: string[];
  };
}

// ============================================================================
// Response error handling
// ============================================================================

/**
 * Maps a rejected axios response to a user-friendly error. Exported for unit
 * testing; registered as the axios response-error interceptor below.
 *
 * The 401 branch distinguishes two very different cases:
 *  - An *authenticated* request (one that carried an Authorization header) that
 *    the server rejected with 401 → a genuinely expired/invalid session. Clear
 *    auth and redirect to /login.
 *  - A 401 from an *unauthenticated* request (login, signup, password reset —
 *    no token present) → the server is reporting a request-level failure such
 *    as bad credentials. Pass the error through unchanged so callers can read
 *    `response.data.detail` (e.g. "Incorrect email or password"). Do NOT clear
 *    auth or redirect — there is no session to expire.
 */
export function handleApiResponseError(error: unknown) {
  if (isApiError(error)) {
    if (error.response?.status === 401) {
      // The request interceptor only sets Authorization when a token exists, so
      // its presence tells us whether this call was made on behalf of a session.
      const wasAuthenticated = Boolean(error.config?.headers?.Authorization);

      if (wasAuthenticated) {
        // Genuine expired/invalid session — clear auth and redirect to login.
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(new Error(i18n.t('errors.sessionExpired')));
      }

      // Unauthenticated 401 — preserve the backend's error (and its detail) and
      // stay on the current page, with its message in the reader's language.
      error.message = serverErrorText(error.response.data, i18n.t('errors.server'));
      return Promise.reject(error);
    }

    if (error.response?.status === 403) {
      return Promise.reject(new Error(i18n.t('errors.forbidden')));
    }

    if (error.response?.status === 409) {
      // 409 detail is the canonical structured object: { message, existing_id }.
      // (`existing_log_id` is the legacy delegate-only key, kept as a fallback.)
      const detail = error.response?.data?.detail as
        | { message?: string; existing_id?: string | null; existing_log_id?: string | null }
        | undefined;
      const err = new Error(serverErrorText(error.response?.data, i18n.t('errors.conflict')));
      const conflict = err as Error & { existingLogId?: string | null; status?: number };
      conflict.existingLogId = detail?.existing_id ?? detail?.existing_log_id ?? null;
      conflict.status = 409;
      return Promise.reject(err);
    }

    if (error.response?.status === 422 || error.response?.status === 400) {
      // Validation error — our own (one code for the whole body) or pydantic's array
      // of {loc, msg, code}. The message is the backend's reason in plain words, in
      // the reader's language; the raw detail rides along as `technical` for a
      // "Technical details" disclosure (finding 16).
      const detail = error.response?.data?.detail;
      const message = typeof error.response?.data?.code === 'string'
        ? serverErrorText(error.response.data, i18n.t('errors.checkInput'))
        : plainValidationMessage(detail);
      const err = new Error(message) as ValidationError;
      err.status = error.response.status;
      err.technical = typeof detail === 'string' ? detail : JSON.stringify(detail ?? null);
      return Promise.reject(err);
    }

    if (error.response?.status && error.response.status >= 500) {
      return Promise.reject(new Error(serverErrorText(error.response?.data, i18n.t('errors.server'))));
    }

    // Network error (no response received)
    if (!error.response) {
      return Promise.reject(new Error(i18n.t('errors.network')));
    }

    // Anything else the server refused (404, 413, 429…): the same error, its message
    // in the reader's language, so `err.message` never shows the server's English.
    error.message = serverErrorText(error.response.data, i18n.t('errors.server'));
  }

  return Promise.reject(error);
}

// ============================================================================
// API Client
// ============================================================================

class APIClient {
  private client: AxiosInstance;

  // Generic HTTP methods for components that call api.post()/api.get() directly
  async post(url: string, data?: unknown) {
    const response = await this.client.post(url, data);
    return response.data;
  }

  async get(url: string, config?: AxiosRequestConfig) {
    const response = await this.client.get(url, config);
    return response.data;
  }

  async put(url: string, data?: unknown) {
    const response = await this.client.put(url, data);
    return response.data;
  }

  async patch(url: string, data?: unknown) {
    const response = await this.client.patch(url, data);
    return response.data;
  }

  async delete(url: string) {
    const response = await this.client.delete(url);
    return response.data;
  }

  constructor() {
    this.client = axios.create({
      baseURL: '/api',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor: add auth token
    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('authToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Response interceptor: handle errors with user-friendly messages
    this.client.interceptors.response.use(
      (response) => response,
      handleApiResponseError
    );
  }

  // ========================================================================
  // Auth Endpoints
  // ========================================================================

  async signup(
    email: string,
    password: string,
    fullName: string,
    dateFormat?: User['date_format'],
    units?: { lab_unit: User['lab_unit']; weight_unit: User['weight_unit'] },
    language?: User['language'],
  ) {
    const response = await this.client.post('/auth/signup', {
      email,
      password,
      full_name: fullName,
      ...(dateFormat ? { date_format: dateFormat } : {}),
      ...(units ?? {}),
      ...(language ? { language } : {}),
    });
    return response.data as AuthResponse;
  }

  /** Fetch the authenticated user (e.g. to pick up a changed date_format). */
  async getMe(): Promise<User> {
    const response = await this.client.get('/auth/me');
    return response.data as User;
  }

  /** Partially update the authenticated user's profile (date format, name). */
  async updateProfile(patch: {
    date_format?: User['date_format'];
    weight_unit?: User['weight_unit'];
    lab_unit?: User['lab_unit'];
    language?: User['language'];
    full_name?: string;
  }): Promise<User> {
    const response = await this.client.patch('/auth/me', patch);
    return response.data as User;
  }

  async login(email: string, password: string) {
    const response = await this.client.post('/auth/login', {
      email,
      password,
    });
    return response.data as AuthResponse;
  }

  // ── Email verification (Phase 2) ──────────────────────────────────────
  async verifyEmail(token: string): Promise<{ message: string }> {
    const response = await this.client.post('/auth/verify-email', { token });
    return response.data;
  }

  async resendVerificationEmail(): Promise<{ message: string }> {
    const response = await this.client.post('/auth/resend-verification');
    return response.data;
  }

  // ── Password reset (Phase 3) ──────────────────────────────────────────
  async forgotPassword(email: string): Promise<{ message: string }> {
    const response = await this.client.post('/auth/forgot-password', { email });
    return response.data;
  }

  async resetPassword(token: string, newPassword: string): Promise<{ message: string }> {
    const response = await this.client.post('/auth/reset-password', {
      token,
      new_password: newPassword,
    });
    return response.data;
  }

  // ── Onboarding gate (Phase 4) ─────────────────────────────────────────
  async completeOnboarding(): Promise<User> {
    const response = await this.client.post('/auth/complete-onboarding');
    return response.data as User;
  }

  // ========================================================================
  // Cat Endpoints
  // ========================================================================

  async getCats(): Promise<Cat[]> {
    const response = await this.client.get('/cats');
    const data = response.data;
    return Array.isArray(data) ? data : [];
  }

  async createCat(data: {
    name: string;
    sex?: 'male' | 'female' | null;
    breed?: string | null;
    date_of_birth?: string | null;
    weight_at_diagnosis: number | null;
    /** Scale the diagnosis weight was taken on — required with a weight. */
    diagnosis_weight_method?: 'home_scale' | 'vet_scale' | null;
    diagnosis_date: string;
    diagnosis_date_precision?: string;
    initial_iris_stage: number | null;
    comorbidities?: unknown[];
    initial_lab_results?: {
      creatinine?: number;
      creatinine_unit?: string;
      sdma?: number | null;
      bun?: number | null;
      bun_unit?: string;
      phosphorus?: number | null;
      phosphorus_unit?: string;
      upc_ratio?: number | null;
      systolic_bp?: number | null;
      potassium?: number | null;
      date?: string;
    };
  }) {
    const response = await this.client.post('/cats', data);
    return response.data as Cat;
  }

  async updateCat(
    catId: string,
    data: Partial<{
      name: string;
      breed: string | null;
      date_of_birth: string | null;
      weight_at_diagnosis: number | null;
      diagnosis_weight_method: 'home_scale' | 'vet_scale' | null;
      diagnosis_date: string;
      diagnosis_date_precision: 'exact' | 'month';
      initial_iris_stage: number | null;
      current_iris_stage: number | null;
      comorbidities: unknown[];
    }>
  ) {
    const response = await this.client.put(`/cats/${catId}`, data);
    return response.data as Cat;
  }

  /** Archive a cat's record — stops every reminder for it. Reversible; deletes nothing. */
  async archiveCat(catId: string): Promise<Cat> {
    const response = await this.client.post(`/cats/${catId}/archive`);
    return response.data as Cat;
  }

  /** Restore an archived record. Reminders resume from here. */
  async unarchiveCat(catId: string): Promise<Cat> {
    const response = await this.client.post(`/cats/${catId}/unarchive`);
    return response.data as Cat;
  }

  // ========================================================================
  async uploadCatPhoto(catId: string, file: File): Promise<Cat> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.client.post(`/cats/${catId}/photo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data as Cat;
  }

  async deleteCatPhoto(catId: string): Promise<void> {
    await this.client.delete(`/cats/${catId}/photo`);
  }

  // Lab Results Endpoints
  // ========================================================================

  async getLabResults(catId: string): Promise<LabResult[]> {
    const response = await this.client.get(`/cats/${catId}/lab-results`);
    const data = response.data;
    return Array.isArray(data) ? data : [];
  }

  /**
   * Out-of-range findings for the cat's most recent lab result. Derived on the
   * fly from current data (never persisted); each carries a display-ready
   * `text` sentence to render verbatim.
   */
  async getLabFindings(catId: string): Promise<LabFinding[]> {
    const response = await this.client.get(`/cats/${catId}/lab-findings`);
    const data = response.data;
    return Array.isArray(data) ? data : [];
  }

  async createLabResult(
    catId: string,
    data: {
      date: string;
      creatinine?: number;
      creatinine_unit?: string;
      sdma?: number;
      bun?: number;
      phosphorus?: number;
      potassium?: number;
      calcium_total?: number;
      calcium_ionized?: number;
      pcv_haematocrit?: number;
      upc_ratio?: number;
      urine_specific_gravity?: number;
      systolic_bp?: number;
      fgf23?: number;
      source?: string;
      notes?: string;
    }
  ) {
    const response = await this.client.post(`/cats/${catId}/lab-results`, data);
    return response.data as LabResult;
  }

  // ========================================================================
  // Lab OCR Endpoints
  // ========================================================================

  /** Every page of one report — photos and/or PDFs — read in a single request. */
  async scanLabPhoto(files: File[]): Promise<LabOCRResponse> {
    const formData = new FormData();
    files.forEach((f) => formData.append('files', f));
    const response = await this.client.post('/lab-ocr/scan', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000, // 60s — vision API can be slow
    });
    return response.data as LabOCRResponse;
  }

  async deleteLabResult(catId: string, resultId: string): Promise<void> {
    await this.client.delete(`/cats/${catId}/lab-results/${resultId}`);
  }

  // ========================================================================
  // Daily Logs Endpoints
  // ========================================================================

  async getDailyLogs(catId: string): Promise<DailyLog[]> {
    const response = await this.client.get(`/cats/${catId}/daily-logs`);
    const data = response.data;
    return Array.isArray(data) ? data : [];
  }

  async createDailyLog(
    catId: string,
    data: {
      date: string;
      appetite_score?: number;
      food_type?: string;
      water_intake_observation?: 'normal' | 'increased' | 'decreased';
      vomiting?: boolean;
      vomiting_count?: number;
      energy_level?: number;
      medications_given?: Record<string, boolean>;
      subq_fluids_administered?: boolean;
      subq_fluids_volume_ml?: number;
      notes?: string;
    }
  ) {
    const response = await this.client.post(`/cats/${catId}/daily-logs`, data);
    return response.data as DailyLog;
  }

  async updateDailyLog(
    catId: string,
    logId: string,
    data: {
      appetite_score?: number;
      food_type?: string;
      water_intake_observation?: 'normal' | 'increased' | 'decreased';
      vomiting?: boolean;
      vomiting_count?: number;
      energy_level?: number;
      defecation?: boolean;
      defecation_count?: number;
      medications_given?: Record<string, boolean>;
      subq_fluids_administered?: boolean;
      subq_fluids_volume_ml?: number;
      subq_fluids_site?: string;
      subq_fluids_absorption?: string;
      subq_fluids_comfort?: string;
      notes?: string;
    }
  ) {
    const response = await this.client.put(`/cats/${catId}/daily-logs/${logId}`, data);
    return response.data as DailyLog;
  }

  async deleteDailyLog(catId: string, logId: string): Promise<void> {
    await this.client.delete(`/cats/${catId}/daily-logs/${logId}`);
  }

  // ========================================================================
  // Care Events (per-event daily-care stream — ADR C9)
  // ========================================================================

  async getCareEvents(catId: string, date?: string): Promise<CareEvent[]> {
    const response = await this.client.get(`/cats/${catId}/care-events`, {
      params: date ? { date } : undefined,
    });
    return Array.isArray(response.data) ? (response.data as CareEvent[]) : [];
  }

  async createCareEvent(catId: string, data: CreateCareEventInput): Promise<CareEvent> {
    const response = await this.client.post(`/cats/${catId}/care-events`, data);
    return response.data as CareEvent;
  }

  async updateCareEvent(
    catId: string,
    eventId: string,
    data: Partial<Pick<CreateCareEventInput, 'date' | 'occurred_at' | 'detail' | 'medication_id'>>,
  ): Promise<CareEvent> {
    const response = await this.client.put(`/cats/${catId}/care-events/${eventId}`, data);
    return response.data as CareEvent;
  }

  async deleteCareEvent(catId: string, eventId: string): Promise<void> {
    await this.client.delete(`/cats/${catId}/care-events/${eventId}`);
  }

  /** All SubQ-fluid sessions, latest first (the trends view derives per-day volume
   *  and the site-history strip from these). Higher limit than the mixed feed so a
   *  heavy fluids-giver's history isn't silently truncated. */
  async getSubqFluidsEvents(catId: string, limit = 1000): Promise<CareEvent[]> {
    const response = await this.client.get(`/cats/${catId}/care-events`, {
      params: { event_type: 'subq_fluids', limit },
    });
    return Array.isArray(response.data) ? (response.data as CareEvent[]) : [];
  }

  /** All blood-glucose readings, latest first (the day-curve and multi-day trend derive
   *  from these). Higher limit than the mixed feed so a curve day's many readings — plus
   *  history — aren't silently truncated. */
  async getGlucoseEvents(catId: string, limit = 1000): Promise<CareEvent[]> {
    const response = await this.client.get(`/cats/${catId}/care-events`, {
      params: { event_type: 'glucose', limit },
    });
    return Array.isArray(response.data) ? (response.data as CareEvent[]) : [];
  }

  /** All resting/sleeping breathing-rate readings, latest first (the trend + the "last time"
   *  context derive from these). */
  async getRestingRateEvents(catId: string, limit = 1000): Promise<CareEvent[]> {
    const response = await this.client.get(`/cats/${catId}/care-events`, {
      params: { event_type: 'resting_respiratory_rate', limit },
    });
    return Array.isArray(response.data) ? (response.data as CareEvent[]) : [];
  }

  /** All defecation episodes, latest first (the litter trend derives per-day frequency
   *  from these — the sibling of getUrinationEvents). */
  async getDefecationEvents(catId: string, limit = 1000): Promise<CareEvent[]> {
    const response = await this.client.get(`/cats/${catId}/care-events`, {
      params: { event_type: 'defecation', limit },
    });
    return Array.isArray(response.data) ? (response.data as CareEvent[]) : [];
  }

  /** All urination episodes, latest first (G11). Feeds the litter trend beside defecation;
   *  "don't know" events (detail.observed=false) carry no count. */
  async getUrinationEvents(catId: string, limit = 1000): Promise<CareEvent[]> {
    const response = await this.client.get(`/cats/${catId}/care-events`, {
      params: { event_type: 'urination', limit },
    });
    return Array.isArray(response.data) ? (response.data as CareEvent[]) : [];
  }

  /** Per-site last-used facts for the rotation suggestion (the pure LRU rule in
   *  lib/subqRotation turns this into the "Suggested" site). */
  async getSubqSiteHistory(catId: string): Promise<SubqSiteHistoryEntry[]> {
    const response = await this.client.get(`/cats/${catId}/subq-fluids/site-history`);
    const sites = (response.data as { sites?: SubqSiteHistoryEntry[] })?.sites;
    return Array.isArray(sites) ? sites : [];
  }

  // ========================================================================
  // Weight Logs Endpoints
  // ========================================================================

  async getWeightLogs(catId: string): Promise<WeightLog[]> {
    const response = await this.client.get(`/cats/${catId}/weight-logs`);
    const data = response.data;
    return Array.isArray(data) ? data : [];
  }

  async createWeightLog(
    catId: string,
    data: {
      date: string;
      weight_kg: number;
      measurement_method?: 'home_scale' | 'vet_scale';
    }
  ) {
    const response = await this.client.post(`/cats/${catId}/weight-logs`, data);
    return response.data as WeightLog;
  }

  async deleteWeightLog(catId: string, logId: string): Promise<void> {
    await this.client.delete(`/cats/${catId}/weight-logs/${logId}`);
  }

  /**
   * The derived weight state — THE single source for every weight figure the UI
   * shows. Returns null when the cat has no readings. Throws if the clinical
   * engine is unreachable (503), which callers surface as "trend unavailable"
   * rather than falling back to a locally computed number that would disagree
   * with the vet sheet.
   */
  async getWeightTrend(catId: string): Promise<WeightTrendResult | null> {
    const response = await this.client.get(`/cats/${catId}/weight-trend`);
    return (response.data ?? null) as WeightTrendResult | null;
  }

  // ========================================================================
  // Quality of Life Assessments Endpoints
  // ========================================================================

  async getQoLAssessments(catId: string): Promise<QoLAssessment[]> {
    const response = await this.client.get(`/cats/${catId}/qol-assessments`);
    const data = response.data;
    return Array.isArray(data) ? data : [];
  }

  async createQoLAssessment(
    catId: string,
    data: {
      date: string;
      appetite_score: number;
      energy_score: number;
      comfort_score: number;
      social_engagement_score: number;
      overall_score: number;
      is_monthly_deep?: boolean;
      grooming_score?: number;
      vomiting_frequency?: string;
      litter_box_notes?: string;
      medication_tolerance_notes?: string;
      composite_score?: number;
    }
  ) {
    const response = await this.client.post(`/cats/${catId}/qol-assessments`, data);
    return response.data as QoLAssessment;
  }

  // ========================================================================
  // Dashboard Endpoints
  // ========================================================================

  async getDashboard(catId: string) {
    const response = await this.client.get(`/cats/${catId}/dashboard`);
    return response.data as DashboardData;
  }

  // ========================================================================
  // Guidance Endpoints
  // ========================================================================

  async getGuidance(catId: string) {
    const response = await this.client.get(`/cats/${catId}/guidance`);
    return response.data as GuidanceData;
  }

  // ========================================================================
  // Education Content Endpoints
  // ========================================================================

  /**
   * Fetch education content by slug from the backend (optional fallback).
   * The primary path uses client-side bundled content for offline support.
   */
  async getEducationContent(catId: string, slug: string) {
    const response = await this.client.get(`/cats/${catId}/education/${slug}`);
    return response.data as {
      entry: {
        slug: string;
        title: string;
        summary: string;
        citation_keys: string[];
        version: number;
      };
      context_header?: string;
      citations: string[];
    };
  }

  // ========================================================================
  // Vet Prep Endpoints
  // ========================================================================

  async getVetPrep(
    catId: string,
    params?: {
      since_date?: string;
    }
  ) {
    const response = await this.client.get(`/cats/${catId}/vet-prep`, {
      params,
    });
    return response.data as VetPrepData;
  }

  // ========================================================================
  // Export Endpoints
  // ========================================================================

  async exportData(catId: string) {
    const response = await this.client.get(`/cats/${catId}/export`, {
      responseType: 'blob',
    });
    return response.data as Blob;
  }

  // ========================================================================
  // Medications Endpoints
  // ========================================================================

  async getMedications(catId: string, params?: { active_only?: boolean }) {
    const response = await this.client.get(`/cats/${catId}/medications`, { params });
    return response.data as unknown[];
  }

  async createMedication(catId: string, data: {
    drug_name: string;
    dose?: number;
    dose_unit?: string;
    frequency: string;
    route: string;
    start_date: string;
    category: string;
    prescribed_by?: string;
    notes?: string;
  }) {
    const response = await this.client.post(`/cats/${catId}/medications`, data);
    return response.data;
  }

  async updateMedication(catId: string, medicationId: string, data: Record<string, unknown>) {
    const response = await this.client.put(`/cats/${catId}/medications/${medicationId}`, data);
    return response.data;
  }

  async deleteMedication(catId: string, medicationId: string) {
    await this.client.delete(`/cats/${catId}/medications/${medicationId}`);
  }

  // ========================================================================
  // Vet Visits Endpoints
  // ========================================================================

  async getVetVisits(catId: string): Promise<VetVisit[]> {
    const response = await this.client.get(`/cats/${catId}/vet-visits`);
    const data = response.data;
    return Array.isArray(data) ? data : [];
  }

  async getVetVisit(catId: string, visitId: string) {
    const response = await this.client.get(`/cats/${catId}/vet-visits/${visitId}`);
    return response.data as VetVisit;
  }

  async createVetVisit(catId: string, data: {
    visit_date: string;
    next_appointment_date?: string | null;
    notes?: string | null;
    medication_changes?: string | null;
  }) {
    const response = await this.client.post(`/cats/${catId}/vet-visits`, data);
    return response.data as VetVisit;
  }

  async updateVetVisit(catId: string, visitId: string, data: {
    visit_date?: string;
    next_appointment_date?: string | null;
    notes?: string | null;
    medication_changes?: string | null;
  }) {
    const response = await this.client.put(`/cats/${catId}/vet-visits/${visitId}`, data);
    return response.data as VetVisit;
  }

  async deleteVetVisit(catId: string, visitId: string) {
    await this.client.delete(`/cats/${catId}/vet-visits/${visitId}`);
  }

  // ========================================================================
  // Vet Questions Endpoints
  // ========================================================================

  async getVetQuestions(catId: string, unansweredOnly = true): Promise<VetQuestion[]> {
    const response = await this.client.get(`/cats/${catId}/vet-questions`, {
      params: { unanswered_only: unansweredOnly },
    });
    const data = response.data;
    return Array.isArray(data) ? data : [];
  }

  async createVetQuestion(catId: string, questionText: string) {
    const response = await this.client.post(`/cats/${catId}/vet-questions`, {
      question_text: questionText,
    });
    return response.data as VetQuestion;
  }

  async updateVetQuestion(catId: string, questionId: string, data: {
    question_text?: string;
    is_answered?: boolean;
  }) {
    const response = await this.client.put(`/cats/${catId}/vet-questions/${questionId}`, data);
    return response.data as VetQuestion;
  }

  async deleteVetQuestion(catId: string, questionId: string) {
    await this.client.delete(`/cats/${catId}/vet-questions/${questionId}`);
  }

  // ========================================================================
  // Care Passes Endpoints
  // ========================================================================

  async createCarePass(catId: string, data: {
    delegate_name: string;
    delegate_email?: string;
    starts_at: string;
    ends_at: string;
    scope_daily_log?: boolean;
    scope_weight?: boolean;
    scope_view_meds?: boolean;
    scope_log_fluids?: boolean;
  }): Promise<CarePass> {
    const response = await this.client.post(`/cats/${catId}/care-passes`, data);
    return response.data as CarePass;
  }

  async listCarePasses(catId: string, status?: string): Promise<CarePass[]> {
    const params = status ? { status } : {};
    const response = await this.client.get(`/cats/${catId}/care-passes`, { params });
    return response.data as CarePass[];
  }

  async getCarePass(catId: string, passId: string): Promise<CarePassDetail> {
    const response = await this.client.get(`/cats/${catId}/care-passes/${passId}`);
    return response.data as CarePassDetail;
  }

  async updateCarePass(catId: string, passId: string, data: {
    delegate_name?: string;
    delegate_email?: string;
    starts_at?: string;
    ends_at?: string;
    scope_daily_log?: boolean;
    scope_weight?: boolean;
    scope_view_meds?: boolean;
    scope_log_fluids?: boolean;
  }): Promise<CarePass> {
    const response = await this.client.patch(`/cats/${catId}/care-passes/${passId}`, data);
    return response.data as CarePass;
  }

  async revokeCarePass(catId: string, passId: string): Promise<CarePass> {
    const response = await this.client.post(`/cats/${catId}/care-passes/${passId}/revoke`);
    return response.data as CarePass;
  }

  async deleteCarePass(catId: string, passId: string): Promise<void> {
    await this.client.delete(`/cats/${catId}/care-passes/${passId}`);
  }
}

// Create and export singleton instance
export const api = new APIClient();
